import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './bulkChangeSectionDialog.module.scss'
import { useMutation } from '@apollo/client'
import { UPDATE_CHILDREN_LINK } from '@/modules/API/parent/childHandler'
import { useNotification } from '@/modules/context/NotificationContext'
import { DaycareType } from '@/types/Daycare'
import { Section } from '@/types/Section'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'

interface BulkChangeSectionDialogProps {
    handleCloseDialog: () => void
    selectedChildIds: number[]
    sectionsList: Section[]
    onSuccess: () => void
}

const BulkChangeSectionDialog: FunctionComponent<BulkChangeSectionDialogProps> = ({
    handleCloseDialog,
    selectedChildIds,
    sectionsList,
    onSuccess
}: BulkChangeSectionDialogProps) => {
    const { t } = useTranslation()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const [selectedSection, setSelectedSection] = useState<string>('')

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const selectOptionsSection: SelectOptionsDeprecated = [
        {
            options: sectionsList.map(
                (section: Section): SelectOptionDeprecated => ({
                    name: section.name,
                    value: section.id.toString()
                })
            )
        }
    ]

    const [updateChildrenLink] = useMutation(UPDATE_CHILDREN_LINK)

    async function handleConfirm(): Promise<void> {
        if (!selectedSection) return

        setIsSubmitting(true)

        try {
            await updateChildrenLink({
                variables: {
                    daycareId: daycare.id,
                    childIds: selectedChildIds,
                    sectionIds: [parseInt(selectedSection)],
                    primarySectionId: parseInt(selectedSection)
                    // complement/groupId intentionally omitted: the bulk mutation leaves each child's own
                    // complement and priority group untouched when these fields are not provided.
                }
            })

            showNotificationSuccess(t('bulkChangeSectionSuccess'))
            onSuccess()
            handleCloseDialog()
        } catch {
            showNotificationError(t('bulkChangeSectionError'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <p className={styles.Text}>
                {selectedChildIds.length} {selectedChildIds.length > 1 ? t('childsSelected') : t('childSelected')}
            </p>
            <div className={styles.SelectContainer}>
                <SelectUnique
                    selectOptions={selectOptionsSection}
                    selectValue={selectedSection}
                    setSelectValue={setSelectedSection}
                    label={isRpeOrLaep ? t('newSector') : t('newSection')}
                    className={styles.UserInput}
                    required
                />
            </div>
            <DialogActions>
                <ButtonTextIcon
                    type={'confirm'}
                    onClick={handleConfirm}
                    disabled={isSubmitting || !selectedSection}
                    nowrap
                />
                <ButtonTextIcon type={'cancel'} onClick={handleCloseDialog} disabled={isSubmitting} nowrap />
            </DialogActions>
        </>
    )
}

export default BulkChangeSectionDialog
